import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ApplicationIcon,
  Button,
  Icon,
  Menu,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { SodWidget } from '../../../../../shared/components';
import SodRadialGauge from '../../../../../shared/components/SodRadialGauge/SodRadialGauge';
import {
  type BarChartData,
  StackedBarChart,
} from '../../../../../shared/components/StackedBarChart';
import { useInvertedLocalize } from '../../../../../shared/hooks';
import { globalActions } from '../../../../../shared/store/actions';
import {
  applicationLogoMap,
  categoryCostCenterChartData,
  categoryRegionChartData,
  departmentChartData,
  segmentColors,
  segmentGradients,
  vendorChartData,
} from '../../../mock-data/graphicalViewMockData';

import {
  CATEGORY_MENU_OPTIONS,
  CATEGORY_OPTIONS,
  CHART_HEIGHT,
  CHART_WIDTH,
  DEFAULT_CATEGORY_OPTION,
  DEFAULT_GROUP_BY_OPTION,
  DEFAULT_METRIC_OPTION,
  DEFAULT_TIME_PERIOD_OPTION,
  GAUGE_CONFIG,
  GAUGE_LEGEND_ITEMS,
  GROUP_BY_OPTIONS,
  METRIC_MENU_OPTIONS,
  OPTIMIZATION_WIDGETS,
  OVERLAY_METRIC_CONFIG,
  STATS_CONFIG,
  TIME_PERIOD_MENU_OPTIONS,
  TOGGLE_BUTTONS,
} from './constants';
import GraphicalViewWidgetMenu from './GraphicalViewWidgetMenu';

import './GraphicalView.css';

/** Renders the application icon for a chart bar's x-axis label. */
function XAxisAppIcon({ item }: { item: BarChartData }): React.ReactElement {
  const logo = applicationLogoMap[item.label] || item.label;

  return <ApplicationIcon logo={logo} size='small' />;
}

const iconXAxisFormatter = (
  _index: number,
  item: BarChartData
): React.ReactElement => <XAxisAppIcon item={item} />;

const textXAxisFormatter = (_index: number, item: BarChartData): string =>
  item.label;

interface GraphicalViewProps {
  saviChartOnly?: boolean;
  saviWidgetsOnly?: boolean;
}

/**
 * GraphicalView - renders license management dashboard charts and widgets
 */
function GraphicalView({
  saviChartOnly = false,
  saviWidgetsOnly = false,
}: GraphicalViewProps): React.ReactElement {
  const localize = useInvertedLocalize();
  const dispatch = useDispatch();

  // Widget customization state
  const [isWidgetMenuOpen, setIsWidgetMenuOpen] = useState(false);
  const [widgets, setWidgets] = useState([
    {
      id: 'software_obsolescence',
      label: 'Software Obsolescence',
      isChecked: true,
    },
    {
      id: 'industry_benchmarks',
      label: 'Industry Benchmarks',
      isChecked: true,
    },
    {
      id: 'apps_waste',
      label: 'Applications With Most License Waste',
      isChecked: true,
    },
    {
      id: 'users_waste',
      label: 'Users With Most License Waste',
      isChecked: true,
    },
    {
      id: 'vendors_dormant',
      label: 'Vendors With Most Dormant Users',
      isChecked: true,
    },
    {
      id: 'licenses_unassigned',
      label: 'Licenses with Most Unassigned Seats',
      isChecked: true,
    },
  ]);

  useEffect(() => {
    dispatch(globalActions.setIsGlobalLoading(false));
  }, [dispatch]);

  const [groupByOption, setGroupByOption] = useState(DEFAULT_GROUP_BY_OPTION);
  const [categoryOption, setCategoryOption] = useState(DEFAULT_CATEGORY_OPTION);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [metricOption, setMetricOption] = useState(DEFAULT_METRIC_OPTION);
  const [isMetricMenuExpanded, setIsMetricMenuExpanded] = useState(false);
  const [timePeriodOption, setTimePeriodOption] = useState(
    DEFAULT_TIME_PERIOD_OPTION
  );
  const [isTimePeriodMenuExpanded, setIsTimePeriodMenuExpanded] =
    useState(false);

  let chartData = vendorChartData;

  if (groupByOption === GROUP_BY_OPTIONS.DEPARTMENT) {
    chartData = departmentChartData;
  } else if (groupByOption === GROUP_BY_OPTIONS.CATEGORY) {
    chartData =
      categoryOption === CATEGORY_OPTIONS.REGION
        ? categoryRegionChartData
        : categoryCostCenterChartData;
  }

  const xAxisConfig =
    groupByOption === GROUP_BY_OPTIONS.DEPARTMENT ||
    groupByOption === GROUP_BY_OPTIONS.CATEGORY
      ? { type: 'text' as const, formatter: textXAxisFormatter }
      : { type: 'icon' as const, formatter: iconXAxisFormatter };

  const timePeriodLabel =
    TIME_PERIOD_MENU_OPTIONS.find((o) => o.value === timePeriodOption)?.label ??
    timePeriodOption;

  const rootClass = cx('GraphicalView', {
    'GraphicalView--saviChartOnly': saviChartOnly,
    'GraphicalView--saviWidgetsOnly': saviWidgetsOnly,
  });

  return (
    <div className={rootClass}>
      {/* Widget Menu Modal — only needed when widget rows are visible */}
      {!saviChartOnly && (
        <GraphicalViewWidgetMenu
          isMenuOpen={isWidgetMenuOpen}
          setIsMenuOpen={setIsWidgetMenuOpen}
          widgets={widgets}
          onWidgetsChange={setWidgets}
        />
      )}
      {/* Main License Spend/Usage Card — hidden in widgets-only mode */}
      {!saviWidgetsOnly && (
        <div className='GraphicalView-license-spend'>
          <div className='GraphicalView-header'>
            <div className='GraphicalView-titleSection'>
              <Typography kind='h2'>
                {localize('License Spend/Usage')}
              </Typography>
            </div>

            <div className='GraphicalView-toggleGroup'>
              {TOGGLE_BUTTONS.map((button) => (
                <Button
                  key={button.value}
                  className={cx('GraphicalView-toggleBtn', button.className, {
                    'GraphicalView-toggleBtn--active':
                      groupByOption === button.value,
                  })}
                  onClick={() => setGroupByOption(button.value)}>
                  {button.label}
                </Button>
              ))}
              <Menu
                options={CATEGORY_MENU_OPTIONS}
                trigger={
                  <Button
                    className={cx('GraphicalView-categoryMenuBtn', {
                      'GraphicalView-categoryMenuBtn--active':
                        groupByOption === GROUP_BY_OPTIONS.CATEGORY,
                    })}>
                    {localize('Category')}
                    <Icon kind='ChevronDown' size='small' />
                  </Button>
                }
                isMenuExpanded={isMenuExpanded}
                setIsExpanded={setIsMenuExpanded}
                onChange={(option: MenuOption) => {
                  setGroupByOption(GROUP_BY_OPTIONS.CATEGORY);
                  setCategoryOption(
                    option.value === CATEGORY_OPTIONS.COST_CENTER
                      ? CATEGORY_OPTIONS.COST_CENTER
                      : CATEGORY_OPTIONS.REGION
                  );
                }}
                shouldCloseMenuOnItemClick
                isOutsideClickEnabled
                size='small'
                placement='bottom-start'
                classNames={{
                  dropdownClassName: 'GraphicalView-categoryMenuDropdown',
                }}
              />
            </div>
            <div className='GraphicalView-filterActions'>
              <Menu
                options={METRIC_MENU_OPTIONS}
                trigger={
                  <Button className='GraphicalView-filterMenuBtn'>
                    {metricOption === 'annual-cost'
                      ? 'By Annual Cost'
                      : 'By Utilization'}
                    <Icon kind='ChevronDown' size='small' />
                  </Button>
                }
                isMenuExpanded={isMetricMenuExpanded}
                setIsExpanded={setIsMetricMenuExpanded}
                onChange={(option: MenuOption) => {
                  setMetricOption(option.value);
                }}
                shouldCloseMenuOnItemClick
                isOutsideClickEnabled
                size='small'
                placement='bottom-start'
                classNames={{
                  dropdownClassName: 'GraphicalView-filterMenuDropdown',
                }}
                className='GraphicalView-Menu'
              />
              <Menu
                options={TIME_PERIOD_MENU_OPTIONS}
                trigger={
                  <Button className='GraphicalView-filterMenuBtn'>
                    {timePeriodLabel}
                    <Icon kind='ChevronDown' size='small' />
                  </Button>
                }
                isMenuExpanded={isTimePeriodMenuExpanded}
                setIsExpanded={setIsTimePeriodMenuExpanded}
                onChange={(option: MenuOption) => {
                  setTimePeriodOption(option.value);
                }}
                shouldCloseMenuOnItemClick
                isOutsideClickEnabled
                size='small'
                placement='bottom-start'
                className='GraphicalView-Menu'
                classNames={{
                  dropdownClassName: 'GraphicalView-filterMenuDropdown',
                }}
              />
            </div>
          </div>

          <div className='GraphicalView-content'>
            {/* Left side - Chart and Stats */}
            <div className='GraphicalView-chartSection'>
              {/* Stats */}
              <div className='GraphicalView-stats'>
                {STATS_CONFIG[groupByOption as keyof typeof STATS_CONFIG].map(
                  (stat, index, array) => (
                    <React.Fragment key={stat.label}>
                      <div className='GraphicalView-statItem'>
                        <div className='GraphicalView-statValue'>
                          {stat.value}
                          <div
                            className={cx('GraphicalView-statTrend', {
                              'GraphicalView-statTrend--down':
                                stat.trendType === 'down',
                              'GraphicalView-statTrend--success':
                                stat.trendType !== 'down',
                            })}>
                            <span className='GraphicalView-trendValue'>
                              {stat.trend}
                            </span>
                            <Icon
                              kind={
                                stat.trendType === 'up'
                                  ? 'TrendUp'
                                  : 'TrendDown'
                              }
                              size='small'
                              className='GraphicalView-trendIcon'
                            />
                          </div>
                        </div>
                        <div className='GraphicalView-statLabel'>
                          {stat.label}
                        </div>
                      </div>
                      {index < array.length - 1 && (
                        <div className='GraphicalView-divider' />
                      )}
                    </React.Fragment>
                  )
                )}
              </div>

              {/* Chart */}
              <div className='GraphicalView-chart'>
                <StackedBarChart
                  data={chartData}
                  width={CHART_WIDTH}
                  height={CHART_HEIGHT}
                  showLegend
                  segmentColors={segmentColors}
                  segmentGradients={segmentGradients}
                  xAxisConfig={xAxisConfig}
                  overlayMetric={OVERLAY_METRIC_CONFIG}
                />
              </div>
            </div>

            <div className='gauge-divider' />

            {/* Right side - Radial Gauge and Widgets */}
            <div className='GraphicalView-sidePanel'>
              {/* Radial Gauge */}
              <div className='GraphicalView-gaugeContainer'>
                <div className='GraphicalView-gauge'>
                  <SodRadialGauge
                    value={GAUGE_CONFIG.value}
                    total={GAUGE_CONFIG.total}
                    rotation={GAUGE_CONFIG.rotation}
                    label={GAUGE_CONFIG.label}
                  />
                </div>

                {/* Gauge Legend */}
                <div className='GraphicalView-gaugeLegend'>
                  {GAUGE_LEGEND_ITEMS.map((item) => (
                    <div key={item.logo} className='GraphicalView-legendItem'>
                      <span className='GraphicalView-legendValue'>
                        {item.value}
                      </span>
                      <ApplicationIcon logo={item.logo} size='xSmall' />
                    </div>
                  ))}
                </div>
              </div>

              {/* Optimization Widgets */}
              <div className='GraphicalView-widgets'>
                {OPTIMIZATION_WIDGETS.map((widget) => (
                  <div key={widget.text} className='GraphicalView-widget'>
                    <p className='GraphicalView-widgetText'>{widget.text}</p>
                    <Icon kind='ChevronRight' size='small' />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Divider — only in full graphical mode (not savi split views) */}
      {!saviChartOnly && !saviWidgetsOnly && (
        <div className='GraphicalView-sectionDivider' />
      )}

      {/* Widgets Section — hidden in chart-only mode */}
      {!saviChartOnly && (
        <>
          {/* Widgets Section Header with Add Icon */}
          <div className='GraphicalView-widgetsHeader'>
            <Button
              className='GraphicalView-settingsBtn'
              aria-label='Widget settings'
              onClick={() => setIsWidgetMenuOpen(true)}>
              <Icon kind='Add' size='small' />
            </Button>
          </div>

          {/* Bottom Widgets Row - 3 widgets per row using SodWidget */}
          <section className='GraphicalView-widgetsRow'>
            {/* Render widgets in order, 3 per row */}
            {widgets.slice(0, 3).map((widget) => {
              if (!widget.isChecked) {
                return null;
              }

              if (widget.id === 'software_obsolescence') {
                return (
                  <SodWidget
                    key={widget.id}
                    kind='tabbedMetrics'
                    widgetSource={{
                      label: localize('Software Obsolescence'),
                      value: 'software_obsolescence',
                      icon: 'AlertWarningFilledSeparateColors',
                      endpoint: '/software-obsolescence',
                      kind: 'tabbedMetrics',
                      description: null,
                      isChecked: true,
                      isDisabled: false,
                    }}
                    currentTab={null}
                    totalAmount={1936500}
                    totalLabel={localize('Total at Risk')}
                    tabs={[
                      { label: 'Expired', value: 'expired' },
                      { label: 'Upcoming', value: 'upcoming' },
                    ]}
                    defaultTab='expired'
                    customWidthSpan={4}
                  />
                );
              }

              if (widget.id === 'industry_benchmarks') {
                return (
                  <SodWidget
                    key={widget.id}
                    kind='comparisonMetric'
                    widgetSource={{
                      label: localize('Industry Benchmarks'),
                      value: 'industry_benchmarks',
                      icon: 'Report',
                      endpoint: '/industry-benchmarks',
                      kind: 'comparisonMetric',
                      description: null,
                      isChecked: true,
                      isDisabled: false,
                    }}
                    currentTab={null}
                    customWidthSpan={4}
                  />
                );
              }

              if (widget.id === 'apps_waste') {
                return (
                  <SodWidget
                    key={widget.id}
                    kind='endpoint'
                    widgetSource={{
                      label: localize('Applications With Most License Waste'),
                      value: 'apps_waste',
                      icon: 'Applications',
                      endpoint: '/apps-waste',
                      kind: 'endpoint',
                      description: null,
                      isChecked: true,
                      isDisabled: false,
                    }}
                    currentTab={null}
                    customWidthSpan={4}
                  />
                );
              }

              return null;
            })}
          </section>

          {/* Second Row - 3 more widgets */}
          <section className='GraphicalView-widgetsRow'>
            {/* Render remaining widgets in order */}
            {widgets.slice(3, 6).map((widget) => {
              if (!widget.isChecked) {
                return null;
              }

              if (widget.id === 'users_waste') {
                return (
                  <SodWidget
                    key={widget.id}
                    kind='user'
                    widgetSource={{
                      label: localize('Users With Most License Waste'),
                      value: 'users_waste',
                      icon: 'User',
                      endpoint: '/users-waste',
                      kind: 'user',
                      description: null,
                      isChecked: true,
                      isDisabled: false,
                    }}
                    currentTab={null}
                    customWidthSpan={4}
                  />
                );
              }

              if (widget.id === 'vendors_dormant') {
                return (
                  <SodWidget
                    key={widget.id}
                    kind='endpoint'
                    widgetSource={{
                      label: localize('Vendors With Most Dormant Users'),
                      value: 'vendors_dormant',
                      icon: 'Applications',
                      endpoint: '/vendors-dormant',
                      kind: 'endpoint',
                      description: null,
                      isChecked: true,
                      isDisabled: false,
                    }}
                    currentTab={null}
                    customWidthSpan={4}
                  />
                );
              }

              if (widget.id === 'licenses_unassigned') {
                return (
                  <SodWidget
                    key={widget.id}
                    kind='endpoint'
                    widgetSource={{
                      label: localize('Licenses with Most Unassigned Seats'),
                      value: 'licenses_unassigned',
                      icon: 'Applications',
                      endpoint: '/licenses-unassigned',
                      kind: 'endpoint',
                      description: null,
                      isChecked: true,
                      isDisabled: false,
                    }}
                    currentTab={null}
                    customWidthSpan={4}
                  />
                );
              }

              return null;
            })}
          </section>
        </>
      )}
    </div>
  );
}

GraphicalView.defaultProps = {
  saviChartOnly: undefined,
  saviWidgetsOnly: undefined,
};

export default GraphicalView;
